// Éléments partagés du module finances : référentiels, listes de rattachement, graphiques SVG, actions.
import { useState, type ReactNode } from "react";
import { ApiError } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { money } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Select, useToast } from "@core/ui";

export type PropRef = { id: string; label: string; kind: string; building_id: string | null; owner_id: string | null; furnished: boolean };
export type BuildingRef = { id: string; name: string; in_copropriete: boolean };
export type LeaseRef = { id: string; property_id: string; property_label: string; status: string; start_on: string; tenants: string };
export type OwnerRef = { id: string; display_name: string; kind: string; has_email: boolean };
export type Refs = { properties: PropRef[]; buildings: BuildingRef[]; leases: LeaseRef[]; owners: OwnerRef[] };

export type Category = {
  code: string;
  kind: "recette" | "depense";
  label: string;
  group: string;
  account: string;
  account_label: string;
  deductible: boolean;
  manual: boolean;
  default_recoverable: string;
  hint: string;
};
export type Catalog = {
  categories: Category[];
  contract_kinds: { code: string; label: string }[];
  syndic_line_categories: { code: string; label: string; default_ratio_bp: number; hint: string }[];
  doc_types: { code: string; label: string; document_category: string }[];
  meter_kinds: { code: string; label: string; unit: string }[];
  reading_kinds: { code: string; label: string }[];
};

/** Chemin d'API d'un espace (les onglets d'extension reçoivent l'espace en propriété). */
export const orgApi = (orgId: string, path: string) => `/api/v1/orgs/${orgId}${path.startsWith("/") ? path : `/${path}`}`;

export function useRefs(orgId?: string) {
  const org = useOrg();
  return useGet<Refs>(orgApi(orgId ?? org.orgId, "/finance/references"));
}

export function useCatalog(orgId?: string) {
  const org = useOrg();
  return useGet<Catalog>(orgApi(orgId ?? org.orgId, "/finance/catalog"));
}

export const currentYear = () => new Date().getFullYear();

export function yearOptions(extraFuture = 1) {
  const y = currentYear();
  const out: { value: string; label: string }[] = [];
  for (let i = y + extraFuture; i >= y - 6; i--) out.push({ value: String(i), label: String(i) });
  return out;
}

export function YearSelect({ value, onChange, label = "Année" }: { value: number; onChange: (y: number) => void; label?: string }) {
  return <Select label={label} value={String(value)} options={yearOptions()} onChange={(e) => onChange(Number(e.target.value))} />;
}

/** Montant aligné, signé pour les recettes / dépenses si demandé. */
export function Amount({ cents, kind }: { cents: number | null | undefined; kind?: "recette" | "depense" }) {
  if (cents === null || cents === undefined) return <span className="finances-montant">—</span>;
  const sign = kind === "depense" ? "− " : kind === "recette" ? "+ " : "";
  return <span className={`finances-montant ${kind === "recette" ? "finances-montant-recette" : ""}`}>{`${sign}${money(cents)}`}</span>;
}

export function PropertySelect({
  refs,
  value,
  onChange,
  label = "Logement",
  empty = "Aucun logement précis",
  buildingId,
  error,
}: {
  refs?: Refs;
  value: string;
  onChange: (v: string) => void;
  label?: string;
  empty?: string;
  buildingId?: string;
  error?: string;
}) {
  const props = (refs?.properties ?? []).filter((p) => !buildingId || p.building_id === buildingId);
  return <Select label={label} value={value} error={error} onChange={(e) => onChange(e.target.value)} options={[{ value: "", label: empty }, ...props.map((p) => ({ value: p.id, label: p.label }))]} />;
}

export function BuildingSelect({ refs, value, onChange, label = "Immeuble", empty = "Aucun immeuble", error }: { refs?: Refs; value: string; onChange: (v: string) => void; label?: string; empty?: string; error?: string }) {
  return <Select label={label} value={value} error={error} onChange={(e) => onChange(e.target.value)} options={[{ value: "", label: empty }, ...(refs?.buildings ?? []).map((b) => ({ value: b.id, label: b.name }))]} />;
}

export function LeaseSelect({ refs, value, onChange, propertyId, label = "Bail", empty = "Aucun bail", help }: { refs?: Refs; value: string; onChange: (v: string) => void; propertyId?: string; label?: string; empty?: string; help?: ReactNode }) {
  const leases = (refs?.leases ?? []).filter((l) => !propertyId || l.property_id === propertyId);
  return (
    <Select
      label={label}
      value={value}
      help={help}
      onChange={(e) => onChange(e.target.value)}
      options={[{ value: "", label: empty }, ...leases.map((l) => ({ value: l.id, label: `${l.property_label}${l.tenants ? ` — ${l.tenants}` : ""} (${LEASE_STATUS[l.status] ?? l.status})` }))]}
    />
  );
}

export const LEASE_STATUS: Record<string, string> = { en_signature: "en signature", signe: "signé", actif: "en cours", preavis: "en préavis", termine: "terminé", resilie: "résilié" };

export const RECOVERABLE: Record<string, string> = { non: "Non récupérable", oui: "Récupérable", partiel: "En partie récupérable", a_determiner: "À déterminer" };

export const PAYMENT_METHODS = [
  { value: "virement", label: "Virement" },
  { value: "prelevement", label: "Prélèvement" },
  { value: "cheque", label: "Chèque" },
  { value: "carte", label: "Carte" },
  { value: "especes", label: "Espèces" },
  { value: "autre", label: "Autre" },
];

/** Nombre décimal saisi à la française (« 2,5 ») en points de base ; null si invalide. */
export function percentToBp(input: string): number | null {
  const t = input.replace(/\s|%/g, "").replace(",", ".");
  if (t === "") return 0;
  if (!/^-?\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(parseFloat(t) * 100);
}

export const bpToInput = (bp: number) => (bp / 100).toLocaleString("fr-FR", { maximumFractionDigits: 2 });

/** Exécute une action avec message de réussite, erreur affichée et rafraîchissement des listes. */
export function useAction() {
  const toast = useToast();
  const invalidate = useInvalidate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function run<T>(fn: () => Promise<T>, opts: { success?: string; refresh?: string[] } = {}): Promise<T | undefined> {
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      if (opts.success) toast(opts.success);
      if (opts.refresh?.length) await invalidate(...opts.refresh);
      return r;
    } catch (e) {
      setError(e);
      if (!(e instanceof ApiError) || (e.status !== 422 && e.status !== 409)) toast(e instanceof Error ? e.message : "Action impossible.", "erreur");
      return undefined;
    } finally {
      setBusy(false);
    }
  }
  return { run, busy, error, setError };
}

export const isConflict = (e: unknown) => e instanceof ApiError && e.status === 409;

/** Erreur de champ détectée avant l'envoi (champ obligatoire vide). */
export const fieldErr = (field: string, message: string) => new ApiError(422, "validation", message, [{ field, message }]);

/** Vérifie les champs obligatoires ; renvoie la première erreur ou null. */
export function required(checks: [boolean, string, string][]) {
  const missing = checks.find(([ok]) => !ok);
  return missing ? fieldErr(missing[1], missing[2]) : null;
}

/** Lien de téléchargement d'une pièce via la route contrôlée du socle. */
export const documentUrl = (id: string, download = false) => `/api/v1/documents/${id}/fichier${download ? "?telecharger=true" : ""}`;

export function DocLink({ id, name }: { id: string | null | undefined; name?: string | null }) {
  if (!id) return null;
  return (
    <a href={documentUrl(id)} target="_blank" rel="noreferrer">
      {name || "Ouvrir la pièce"}
    </a>
  );
}

// ---------------------------------------------------------------------------
// Graphiques SVG sans bibliothèque

const W = 720;
const H = 240;
const PAD = { l: 64, r: 12, t: 14, b: 30 };

function niceMax(v: number) {
  if (v <= 0) return 100;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

const shortMoney = (cents: number) => `${Math.round(cents / 100).toLocaleString("fr-FR")} €`;

/** Barres mensuelles recettes / dépenses et courbe du solde cumulé. Le tableau voisin donne les valeurs exactes. */
export function MonthlyChart({ rows, caption }: { rows: { label: string; income: number; expense: number; cumulative: number }[]; caption: string }) {
  const maxV = niceMax(Math.max(1, ...rows.map((r) => Math.max(r.income, r.expense, Math.abs(r.cumulative)))));
  const minCum = Math.min(0, ...rows.map((r) => r.cumulative));
  const minV = minCum < 0 ? -niceMax(-minCum) : 0;
  const plotH = H - PAD.t - PAD.b;
  const y = (v: number) => PAD.t + ((maxV - v) / (maxV - minV)) * plotH;
  const slot = (W - PAD.l - PAD.r) / Math.max(1, rows.length);
  const bw = Math.max(4, slot * 0.32);
  const ticks = [minV, minV < 0 ? 0 : maxV / 2, maxV].filter((v, i, a) => a.indexOf(v) === i);
  const pts = rows.map((r, i) => `${PAD.l + slot * i + slot / 2},${y(r.cumulative)}`).join(" ");
  return (
    <figure className="stack-sm" style={{ margin: 0 }}>
      <svg className="finances-graphique" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={caption}>
        {ticks.map((t) => (
          <g key={t}>
            <line className="finances-trait-zero" x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} strokeOpacity={t === 0 ? 1 : 0.35} />
            <text x={PAD.l - 6} y={y(t) + 4} textAnchor="end">
              {shortMoney(t)}
            </text>
          </g>
        ))}
        {rows.map((r, i) => {
          const x0 = PAD.l + slot * i + slot / 2;
          return (
            <g key={i}>
              <rect className="finances-barre-recette" x={x0 - bw - 1} y={y(r.income)} width={bw} height={Math.max(0, y(0) - y(r.income))} rx={2}>
                <title>{`${r.label} : recettes ${money(r.income)}`}</title>
              </rect>
              <rect className="finances-barre-depense" x={x0 + 1} y={y(r.expense)} width={bw} height={Math.max(0, y(0) - y(r.expense))} rx={2}>
                <title>{`${r.label} : dépenses ${money(r.expense)}`}</title>
              </rect>
              <text x={x0} y={H - 10} textAnchor="middle">
                {r.label.slice(0, 3)}
              </text>
            </g>
          );
        })}
        <polyline className="finances-courbe" points={pts} />
        {rows.map((r, i) => (
          <circle key={i} className="finances-point" cx={PAD.l + slot * i + slot / 2} cy={y(r.cumulative)} r={2.5}>
            <title>{`${r.label} : solde cumulé ${money(r.cumulative)}`}</title>
          </circle>
        ))}
      </svg>
      <figcaption className="finances-legende">
        <span>
          <i className="finances-pastille finances-pastille-recette" aria-hidden="true" /> Encaissements
        </span>
        <span>
          <i className="finances-pastille finances-pastille-depense" aria-hidden="true" /> Paiements
        </span>
        <span>
          <i className="finances-pastille finances-pastille-solde" aria-hidden="true" /> Solde cumulé
        </span>
      </figcaption>
    </figure>
  );
}

/** Barres horizontales prévu / réalisé par poste. */
export function CompareBars({ rows, caption }: { rows: { label: string; planned: number; realized: number }[]; caption: string }) {
  const max = niceMax(Math.max(1, ...rows.flatMap((r) => [r.planned, r.realized])));
  const rowH = 34;
  const left = 190;
  const h = rows.length * rowH + 8;
  const x = (v: number) => (v / max) * (W - left - 90);
  return (
    <figure className="stack-sm" style={{ margin: 0 }}>
      <svg className="finances-graphique" viewBox={`0 0 ${W} ${h}`} role="img" aria-label={caption}>
        {rows.map((r, i) => (
          <g key={r.label} transform={`translate(0, ${i * rowH + 4})`}>
            <text x={left - 8} y={17} textAnchor="end">
              {r.label.length > 28 ? `${r.label.slice(0, 27)}…` : r.label}
            </text>
            <rect className="finances-barre-prevu" x={left} y={3} width={Math.max(1, x(r.planned))} height={11} rx={2}>
              <title>{`${r.label} : prévu ${money(r.planned)}`}</title>
            </rect>
            <rect className="finances-barre-realise" x={left} y={16} width={Math.max(1, x(r.realized))} height={11} rx={2}>
              <title>{`${r.label} : réalisé ${money(r.realized)}`}</title>
            </rect>
            <text x={left + Math.max(x(r.planned), x(r.realized)) + 6} y={20}>
              {shortMoney(r.realized)}
            </text>
          </g>
        ))}
      </svg>
      <figcaption className="finances-legende">
        <span>
          <i className="finances-pastille finances-pastille-prevu" aria-hidden="true" /> Prévu
        </span>
        <span>
          <i className="finances-pastille finances-pastille-recette" aria-hidden="true" /> Réalisé
        </span>
      </figcaption>
    </figure>
  );
}

/** État d'échéance d'un contrat : mot + ton, jamais la couleur seule. */
export function DeadlineBadge({ state }: { state: string }) {
  const map: Record<string, [string, "neutre" | "encre" | "succes" | "attention" | "erreur"]> = {
    actif: ["En cours", "succes"],
    a_renouveler: ["Préavis bientôt atteint", "attention"],
    preavis_depasse: ["Préavis dépassé", "erreur"],
    echu: ["Échu", "neutre"],
    sans_echeance: ["Sans échéance", "neutre"],
  };
  const [label, tone] = map[state] ?? [state, "neutre"];
  return <Badge tone={tone}>{label}</Badge>;
}
